import { type TrackingTicketOptions } from '@root/lib/modules/tracking/tracking.module';

export interface FbPixelAdapterRegisterConfig {
	/** Facebook Pixel tracking ID */
	trackingId: string;
}

/**
 * Facebook Pixel tracking adapter
 * @author filipditrich <ditrich@nfctron.com>
 * @export
 */
export class FbPixelAdapter {
	private readonly allowTracking;
	public fbPixelIds: string[] = [];

	get fbq(): facebook.Pixel.Event | undefined {
		if (typeof window === 'undefined') return undefined;
		return (window as any)?.fbq as facebook.Pixel.Event | undefined;
	}

	constructor(allowTracking: boolean = false) {
		this.allowTracking = allowTracking;
	}

	/**
	 * Registers Facebook Pixel tracking Id
	 * @param {string} trackingId
	 */
	public register = ({ trackingId }: FbPixelAdapterRegisterConfig) => {
		/** ignore when tracking is disallowed */
		if (!this.allowTracking) return;
		/** ignore re-registering the same trackingId */
		if (this.fbPixelIds.includes(trackingId)) return;
		/** construct the script */
		console.debug('🧚🏻 [FbPixelModule] register(%s) called', trackingId);
		/* eslint-disable */
		// @ts-ignore
		!(function (f: any, b, e, v, n: any, t: any, s: any) {
			if (f.fbq) return;
			n = f.fbq = function () {
				n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
			};
			if (!f._fbq) f._fbq = n;
			n.push = n;
			n.loaded = !0;
			n.version = '2.0';
			n.queue = [];
			t = b.createElement(e);
			t.async = !0;
			t.src = v;
			s = b.getElementsByTagName(e)[0];
			s.parentNode.insertBefore(t, s);
		})(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

		/** call the fbq method to register the trackingId */
		this.fbPixelIds.push(trackingId);
		this.fbq?.('init', trackingId);
	};

	/**
	 * Page view handler function
	 * @see https://developers.facebook.com/docs/meta-pixel/get-started
	 */
	public handlePageView = () => {
		if (!this.allowTracking || this.fbPixelIds.length === 0) return;
		console.debug('🧚🏻 [FbPixelModule] handlePageView() called');
		this.fbq?.('track', 'PageView');
	};

	/**
	 * Event handler function
	 *
	 * @param {string} name
	 * @param {facebook.Pixel.ViewContentParameters | facebook.Pixel.SearchParameters | facebook.Pixel.AddToCartParameters | facebook.Pixel.AddToWishlistParameters | facebook.Pixel.InitiateCheckoutParameters | facebook.Pixel.AddPaymentInfoParameters | facebook.Pixel.PurchaseParameters | facebook.Pixel.LeadParameters | facebook.Pixel.CompleteRegistrationParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public handleEvent = (
		name: string,
		options:
			| facebook.Pixel.ViewContentParameters
			| facebook.Pixel.SearchParameters
			| facebook.Pixel.AddToCartParameters
			| facebook.Pixel.AddToWishlistParameters
			| facebook.Pixel.InitiateCheckoutParameters
			| facebook.Pixel.AddPaymentInfoParameters
			| facebook.Pixel.PurchaseParameters
			| facebook.Pixel.LeadParameters
			| facebook.Pixel.CompleteRegistrationParameters,
		ref: Record<string, unknown> = {},
	) => {
		if (!this.allowTracking || this.fbPixelIds.length === 0) return;
		console.debug('🧚🏻 [FbPixelModule] handleEvent("%s") called', name, options, ref);
		this.fbq?.('track', name, options);
	};

	/**
	 * Converts tickets list to Pixel ContentIds structure
	 * @param {Array<TrackingTicketOptions>} tickets
	 * @return {Array<Gtag.Item>}
	 */
	public static convertTicketsToContentIds = (tickets: Array<TrackingTicketOptions>) =>
		tickets.reduce((acc, ticket) => {
			/** add new ticket item */
			acc.push(ticket.ticketType.ticketTypeId);

			/** add chip items */
			if (ticket.chips.length > 0) {
				const ticketChips = ticket.chips.map((chip, index) => chip.productItemId);
				acc.push(...ticketChips);
			}
			/** add extra product items */
			if (ticket.extraProducts.length > 0) {
				const ticketExtraProducts = ticket.extraProducts.reduce((accExtra, extraProduct, index) => {
					accExtra.push(extraProduct.ticketProductId);

					/** add extra product chip items */
					if (extraProduct.chips.length > 0) {
						const extraProductChips = extraProduct.chips.map((chip, indexExtra) => chip.productItemId);
						acc.push(...extraProductChips);
					}

					return accExtra;
				}, [] as Array<string>);
				acc.push(...ticketExtraProducts);
			}
			return acc;
		}, [] as Array<string>);

	/**
	 * InitiateCheckout event handler
	 * @param {facebook.Pixel.InitiateCheckoutParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public initiateCheckout = (options: facebook.Pixel.InitiateCheckoutParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('InitiateCheckout', options, ref);
	};

	/**
	 * Purchase event handler
	 * @param {facebook.Pixel.PurchaseParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public purchase = (options: facebook.Pixel.PurchaseParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('Purchase', options, ref);
	};

	/**
	 * Purchase success event handler
	 * @param {facebook.Pixel.CustomParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public purchaseSuccess = (options: facebook.Pixel.CustomParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('PurchaseSuccess', options, ref);
	};

	/**
	 * Purchase fail event handler
	 * @param {facebook.Pixel.CustomParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public purchaseFail = (options: facebook.Pixel.CustomParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('PurchaseFail', options, ref);
	};

	/**
	 * AddToCart event handler
	 * @param {facebook.Pixel.AddToCartParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public addToCart = (options: facebook.Pixel.AddToCartParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('AddToCart', options, ref);
	};

	/**
	 * RemoveFromCart event handler
	 * @param {facebook.Pixel.CustomParameters} options
	 * @param {Record<string, unknown>} ref
	 */
	public removeFromCart = (options: facebook.Pixel.CustomParameters, ref: Record<string, unknown> = {}) => {
		this.handleEvent('RemoveFromCart', options, ref);
	};
}
